import { replaceNameWithTokenInText } from '../../utils/character-name-tokens.js';

export const KV_KEY_PLACEHOLDER = 'e.g. Personality, Backstory';
export const KV_VAL_PLACEHOLDER = 'e.g. Brave, loyal, witty...';
export const KV_DETAILS_MAX = 3000;
export const KV_VALUE_MAX = 550;

let committedCharacterName = '';
let kvFitLock = 0;
let kvPointerEndBound = false;

const activePointerTextareas = new Set();
const kvResizeObservers = new WeakMap();

const escapeAttrVal = (str) => String(str ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;');

const markKvUserSized = (textarea) => {
  textarea.dataset.kvUserSized = '1';
  delete textarea.dataset.kvFitPending;
};

const endKvPointer = () => {
  for (const textarea of activePointerTextareas) {
    const start = Number(textarea.dataset.kvHeightAtPointer);
    if (kvFitLock === 0 && Number.isFinite(start) && textarea.offsetHeight !== start) {
      markKvUserSized(textarea);
    }
    delete textarea.dataset.kvHeightAtPointer;
  }
  activePointerTextareas.clear();
};

const bindKvPointerEnd = () => {
  if (kvPointerEndBound || typeof window === 'undefined') return;
  kvPointerEndBound = true;
  window.addEventListener('pointerup', endKvPointer);
  window.addEventListener('pointercancel', endKvPointer);
};

const bindKvManualResize = (textarea) => {
  if (textarea.dataset.kvResizeBound === '1') return;
  textarea.dataset.kvResizeBound = '1';
  bindKvPointerEnd();
  textarea.addEventListener('pointerdown', () => {
    activePointerTextareas.add(textarea);
    textarea.dataset.kvHeightAtPointer = String(textarea.offsetHeight);
  });
  const observer = new ResizeObserver(() => {
    if (kvFitLock > 0 || !activePointerTextareas.has(textarea)) return;
    const start = Number(textarea.dataset.kvHeightAtPointer);
    if (!Number.isFinite(start) || textarea.offsetHeight === start) return;
    markKvUserSized(textarea);
  });
  observer.observe(textarea);
  kvResizeObservers.set(textarea, observer);
};

const releaseKvValue = (textarea) => {
  if (!(textarea instanceof HTMLTextAreaElement)) return;
  kvResizeObservers.get(textarea)?.disconnect();
  kvResizeObservers.delete(textarea);
  activePointerTextareas.delete(textarea);
};

export const releaseKvRows = (root) => {
  root?.querySelectorAll('.kv-value').forEach((node) => releaseKvValue(node));
};

const applyKvFit = (textarea) => {
  if (!(textarea instanceof HTMLTextAreaElement)) return;
  if (textarea.dataset.kvUserSized === '1') {
    delete textarea.dataset.kvFitPending;
    return;
  }
  if (!textarea.isConnected || textarea.offsetParent === null) {
    textarea.dataset.kvFitPending = '1';
    return;
  }
  const style = getComputedStyle(textarea);
  const border = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
  const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
  kvFitLock += 1;
  textarea.style.height = '0px';
  const next = style.boxSizing === 'border-box'
    ? textarea.scrollHeight + border
    : Math.max(0, textarea.scrollHeight - padding);
  textarea.style.height = `${next}px`;
  if (activePointerTextareas.has(textarea)) {
    textarea.dataset.kvHeightAtPointer = String(textarea.offsetHeight);
  }
  delete textarea.dataset.kvFitPending;
  requestAnimationFrame(() => {
    kvFitLock -= 1;
  });
};

const scheduleKvFit = (textarea) => {
  if (!(textarea instanceof HTMLTextAreaElement)) return;
  queueMicrotask(() => applyKvFit(textarea));
};

/** Write a KV value and size it to the content once the field is visible. */
export const setKvValue = (textarea, value) => {
  if (!(textarea instanceof HTMLTextAreaElement)) return;
  textarea.value = String(value ?? '');
  scheduleKvFit(textarea);
};

export const flushKvFits = () => {
  const table = document.getElementById('step2-kv-table');
  if (!table) return;
  table.querySelectorAll('.kv-value').forEach((node) => {
    if (!(node instanceof HTMLTextAreaElement) || node.dataset.kvFitPending !== '1') return;
    applyKvFit(node);
  });
};

export const createKvRow = (key = '', value = '') => {
  const row = document.createElement('div');
  row.className = 'step2-kv-row';
  row.setAttribute('role', 'listitem');
  row.innerHTML = `
    <input type="text" class="form-input kv-key" placeholder="${KV_KEY_PLACEHOLDER}" aria-label="Trait or field name" tabindex="0" value="${escapeAttrVal(key)}" />
    <textarea class="form-input kv-value" placeholder="${KV_VAL_PLACEHOLDER}" aria-label="Trait value" tabindex="0" rows="1">${escapeAttrVal(value)}</textarea>
    <button type="button" class="btn step2-kv-delete" aria-label="Remove field" tabindex="0">
      <i class="fas fa-trash" aria-hidden="true"></i>
    </button>
  `;
  const valueEl = row.querySelector('.kv-value');
  if (valueEl instanceof HTMLTextAreaElement) {
    bindKvManualResize(valueEl);
    if (String(value ?? '').trim()) scheduleKvFit(valueEl);
  }
  row.querySelector('.step2-kv-delete').addEventListener('click', () => {
    releaseKvValue(valueEl);
    row.remove();
    refreshKvCounter();
  });
  return row;
};

export const refreshKvCounter = () => {
  const counter = document.getElementById('step2-kv-counter');
  if (!counter) return;
  const length = JSON.stringify(buildKvDetails()).length;
  const showCounter = length >= KV_DETAILS_MAX / 2;
  counter.textContent = `${length}/${KV_DETAILS_MAX}`;
  counter.classList.toggle('is-hidden', !showCounter);
  counter.classList.toggle('limit-reached', length > KV_DETAILS_MAX);
};

export const bindKvTableCounter = () => {
  const table = document.getElementById('step2-kv-table');
  if (!table) return;
  if (table.dataset.kvCountBound !== '1') {
    table.dataset.kvCountBound = '1';
    table.addEventListener('input', () => refreshKvCounter());
  }
  refreshKvCounter();
};

export const populateKvRow = (key, value) => {
  if (value === null || value === undefined) return;
  const strVal = String(value).trim();
  if (!strVal) return;
  const table = document.getElementById('step2-kv-table');
  if (!table) return;
  const normalKey = key.toLowerCase();
  for (const row of table.querySelectorAll('.step2-kv-row')) {
    const keyEl = row.querySelector('.kv-key');
    if (keyEl && keyEl.value.toLowerCase() === normalKey) {
      const valEl = row.querySelector('.kv-value');
      if (valEl instanceof HTMLTextAreaElement) setKvValue(valEl, strVal);
      refreshKvCounter();
      return;
    }
  }
  table.appendChild(createKvRow(key, strVal));
  refreshKvCounter();
};

export const removeEmptyKvRows = () => {
  const table = document.getElementById('step2-kv-table');
  if (!table) return;
  for (const row of table.querySelectorAll('.step2-kv-row')) {
    const keyEmpty = !(row.querySelector('.kv-key')?.value || '').trim();
    const valEmpty = !(row.querySelector('.kv-value')?.value || '').trim();
    if (keyEmpty && valEmpty) {
      releaseKvValue(row.querySelector('.kv-value'));
      row.remove();
    }
  }
  refreshKvCounter();
};

export const initStep2KvTable = (defaultKeys = ['Personality', 'Likes', 'Dislikes']) => {
  const table = document.getElementById('step2-kv-table');
  if (!table) return;
  if (!table.querySelector('.step2-kv-row')) {
    defaultKeys.forEach((key) => table.appendChild(createKvRow(key)));
  }
  bindKvTableCounter();
};

export const buildKvDetails = () => {
  const table = document.getElementById('step2-kv-table');
  if (!table) return /** @type {Record<string, string>} */ ({});
  const details = /** @type {Record<string, string>} */ ({});

  table.querySelectorAll('.step2-kv-row').forEach((row) => {
    const k = (row.querySelector('.kv-key')?.value || '').trim();
    const v = (row.querySelector('.kv-value')?.value || '').trim();
    if (!k || !v) return;
    details[k] = v;
  });
  return details;
};

const rewriteKvCharacterName = (previousName, nextName) => {
  const table = document.getElementById('step2-kv-table');
  if (!table) return;
  table.querySelectorAll('.step2-kv-row').forEach((row) => {
    const keyEl = row.querySelector('.kv-key');
    const valEl = row.querySelector('.kv-value');
    if (keyEl) keyEl.value = replaceNameWithTokenInText(keyEl.value, previousName, nextName);
    if (valEl instanceof HTMLTextAreaElement) {
      setKvValue(valEl, replaceNameWithTokenInText(valEl.value, previousName, nextName));
    }
  });
  refreshKvCounter();
};

/** Record the name already written into the info fields, without rewriting them. */
export const rememberCharacterName = (name) => {
  committedCharacterName = String(name || '').trim();
};

/** Replace the previous committed name in info fields, then store the new name. */
export const commitCharacterNameChange = (name) => {
  const next = String(name || '').trim();
  const prev = committedCharacterName;
  if (prev && next && prev !== next) rewriteKvCharacterName(prev, next);
  committedCharacterName = next;
};

export const applyKvDetails = (details) => {
  if (!details || typeof details !== 'object') return;

  for (const [key, value] of Object.entries(details)) {
    if (value === null || value === undefined) continue;
    populateKvRow(key, String(value));
  }

  const formEl = document.getElementById('step2-form') || document.getElementById('character-form');
  if (formEl) formEl.dispatchEvent(new Event('input', { bubbles: true }));
  refreshKvCounter();
};
